import { type ReactElement, useState } from 'react';
import { type LayoutChangeEvent, useWindowDimensions } from 'react-native';
import {
  Gesture,
  GestureDetector,
  type GestureStateManager,
  type GestureTouchEvent,
  State,
} from 'react-native-gesture-handler';
import Animated, {
  cancelAnimation,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withDecay,
  withTiming,
} from 'react-native-reanimated';
import { constrainTransform, getMaxTranslation } from './ZoomableImageBounds';
import ZoomableImageCursor from './ZoomableImageCursor';
import {
  didReturnToMinimumScale,
  shouldAcceptDoubleTap,
  shouldAcceptSingleTap,
  shouldApplyPanMomentum,
  shouldApplyPinchUpdate,
  shouldRejectSingleFingerGesture,
  shouldSettlePinch,
  updateGestureLock,
} from './ZoomableImageGestureLock';

interface ZoomableImageProps {
  children: ReactElement;
  imageSize?: {
    width: number;
    height: number;
  };
  minScale?: number;
  maxScale?: number;
  doubleTapScale?: number;
  onSingleTap?: () => void;
  onTap?: () => void;
  onDoubleTap?: () => void;
  onPanStart?: () => void;
  onPinchStart?: () => void;
  onReturnToMinimumScale?: () => void;
  singleClickZoom?: boolean;
}

const DOUBLE_TAP_MAX_DURATION_MS = 400;
const DOUBLE_TAP_MAX_DELAY_MS = 350;
export default function ZoomableImage({
  children,
  imageSize,
  minScale = 1,
  maxScale = 8,
  doubleTapScale = 3,
  onSingleTap,
  onTap,
  onDoubleTap,
  onPanStart,
  onPinchStart,
  onReturnToMinimumScale,
  singleClickZoom = false,
}: ZoomableImageProps) {
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();
  const [containerSize, setContainerSize] = useState({
    width: screenWidth,
    height: screenHeight,
  });
  const [isZoomedIn, setIsZoomedIn] = useState(false);
  const scale = useSharedValue(minScale);
  const translateX = useSharedValue(0);
  const translateY = useSharedValue(0);
  const pinchStartScale = useSharedValue(minScale);
  const pinchStartTranslateX = useSharedValue(0);
  const pinchStartTranslateY = useSharedValue(0);
  const pinchStartFocalX = useSharedValue(0);
  const pinchStartFocalY = useSharedValue(0);
  const panStartTranslateX = useSharedValue(0);
  const panStartTranslateY = useSharedValue(0);
  const panStartTime = useSharedValue(0);
  const isPinching = useSharedValue(false);
  const isPinchTransformFrozen = useSharedValue(false);
  const frozenPinchScale = useSharedValue(minScale);
  const frozenPinchTranslateX = useSharedValue(0);
  const frozenPinchTranslateY = useSharedValue(0);
  const didZoomDuringPinch = useSharedValue(false);
  const singleFingerGesturesLocked = useSharedValue(false);
  const singleTapCancelledByPan = useSharedValue(false);
  const activeTouchCount = useSharedValue(0);

  const clamp = (value: number, min: number, max: number) => {
    'worklet';
    return Math.max(min, Math.min(max, value));
  };

  const getMaxTranslate = (currentScale: number) => {
    'worklet';
    return getMaxTranslation(currentScale, {
      width: containerSize.width,
      height: containerSize.height,
      minScale,
      maxScale,
      imageWidth: imageSize?.width,
      imageHeight: imageSize?.height,
    });
  };

  const getConstrainedTransform = (
    currentScale: number,
    currentTranslateX: number,
    currentTranslateY: number,
  ) => {
    'worklet';
    return constrainTransform(
      {
        scale: currentScale,
        translateX: currentTranslateX,
        translateY: currentTranslateY,
      },
      {
        width: containerSize.width,
        height: containerSize.height,
        minScale,
        maxScale,
        imageWidth: imageSize?.width,
        imageHeight: imageSize?.height,
      },
    );
  };

  const isZoomed = (currentScale: number) => {
    'worklet';
    return currentScale > minScale + 0.01;
  };

  const resetAnimation = () => {
    'worklet';
    scale.value = withTiming(minScale, { duration: 350 });
    translateX.value = withTiming(0, { duration: 350 });
    translateY.value = withTiming(0, { duration: 350 });
    if (singleClickZoom) {
      runOnJS(setIsZoomedIn)(false);
    }
  };

  const toggleZoomAtPoint = (x: number, y: number) => {
    'worklet';
    if (isZoomed(scale.value)) {
      resetAnimation();
      if (onReturnToMinimumScale) {
        runOnJS(onReturnToMinimumScale)();
      }
      return;
    }

    cancelAnimation(scale);
    cancelAnimation(translateX);
    cancelAnimation(translateY);

    const targetScale = clamp(doubleTapScale, minScale, maxScale);
    const tapX = x - containerSize.width / 2;
    const tapY = y - containerSize.height / 2;
    const scaleRatio = targetScale / minScale;
    const targetTranslateX = -tapX * (scaleRatio - 1);
    const targetTranslateY = -tapY * (scaleRatio - 1);
    const maxTranslate = getMaxTranslate(targetScale);

    scale.value = withTiming(targetScale, { duration: 300 });
    translateX.value = withTiming(
      clamp(targetTranslateX, -maxTranslate.x, maxTranslate.x),
      { duration: 300 },
    );
    translateY.value = withTiming(
      clamp(targetTranslateY, -maxTranslate.y, maxTranslate.y),
      { duration: 300 },
    );
    if (singleClickZoom) {
      runOnJS(setIsZoomedIn)(true);
    }
  };

  const freezePinchTransform = () => {
    'worklet';
    if (!isPinching.value || isPinchTransformFrozen.value) {
      return;
    }

    cancelAnimation(scale);
    cancelAnimation(translateX);
    cancelAnimation(translateY);
    frozenPinchScale.value = scale.value;
    frozenPinchTranslateX.value = translateX.value;
    frozenPinchTranslateY.value = translateY.value;
    isPinchTransformFrozen.value = true;
  };

  const restoreFrozenPinchTransform = () => {
    'worklet';
    if (!isPinchTransformFrozen.value) {
      return;
    }

    scale.value = frozenPinchScale.value;
    translateX.value = frozenPinchTranslateX.value;
    translateY.value = frozenPinchTranslateY.value;
  };

  const settlePinch = () => {
    'worklet';
    if (!isPinching.value) {
      return;
    }

    restoreFrozenPinchTransform();
    isPinching.value = false;
    isPinchTransformFrozen.value = false;

    const finalScale = clamp(scale.value, minScale, maxScale);
    const maxTranslate = getMaxTranslate(finalScale);
    const finalTranslateX = isZoomed(finalScale)
      ? clamp(translateX.value, -maxTranslate.x, maxTranslate.x)
      : 0;
    const finalTranslateY = isZoomed(finalScale)
      ? clamp(translateY.value, -maxTranslate.y, maxTranslate.y)
      : 0;
    const returnedToMinimum = didReturnToMinimumScale(
      didZoomDuringPinch.value,
      finalScale,
      minScale,
    );
    didZoomDuringPinch.value = false;
    if (singleClickZoom) {
      runOnJS(setIsZoomedIn)(isZoomed(finalScale));
    }

    if (returnedToMinimum && onReturnToMinimumScale) {
      runOnJS(onReturnToMinimumScale)();
    }

    if (finalScale !== scale.value) {
      scale.value = withTiming(finalScale, { duration: 180 });
    }
    if (finalTranslateX !== translateX.value) {
      translateX.value = withTiming(finalTranslateX, { duration: 180 });
    }
    if (finalTranslateY !== translateY.value) {
      translateY.value = withTiming(finalTranslateY, { duration: 180 });
    }
  };

  const rejectSingleFingerGesture = (
    event: GestureTouchEvent,
    stateManager: GestureStateManager,
  ) => {
    'worklet';
    if (
      !shouldRejectSingleFingerGesture(
        singleFingerGesturesLocked.value,
        event.numberOfTouches,
      )
    ) {
      return;
    }

    if (event.numberOfTouches >= 2) {
      activeTouchCount.value = event.numberOfTouches;
      singleFingerGesturesLocked.value = true;
    }

    if (event.state === State.ACTIVE) {
      stateManager.end();
    } else if (
      event.state === State.BEGAN ||
      event.state === State.UNDETERMINED
    ) {
      stateManager.fail();
    }
  };

  const updateTouchLock = (event: GestureTouchEvent) => {
    'worklet';
    const wasLocked = singleFingerGesturesLocked.value;
    const nextLocked = updateGestureLock(wasLocked, event.numberOfTouches);
    activeTouchCount.value = event.numberOfTouches;

    singleFingerGesturesLocked.value = nextLocked;
  };

  const touchTrackerGesture = Gesture.Manual()
    .onTouchesDown((event) => {
      if (event.numberOfTouches === 1) {
        singleTapCancelledByPan.value = false;
      }
      updateTouchLock(event);
    })
    .onTouchesMove((event) => {
      updateTouchLock(event);
      if (event.numberOfTouches < 2) {
        freezePinchTransform();
        restoreFrozenPinchTransform();
      }
    })
    .onTouchesUp((event, stateManager) => {
      updateTouchLock(event);

      if (event.numberOfTouches < 2) {
        freezePinchTransform();
        restoreFrozenPinchTransform();
      }

      if (event.numberOfTouches === 0) {
        settlePinch();
        stateManager.fail();
      }
    })
    .onTouchesCancelled((event, stateManager) => {
      updateTouchLock(event);

      if (event.numberOfTouches < 2) {
        freezePinchTransform();
        restoreFrozenPinchTransform();
      }

      if (event.numberOfTouches === 0) {
        settlePinch();
        stateManager.fail();
      }
    });

  const pinchGesture = Gesture.Pinch()
    .onStart((event) => {
      cancelAnimation(scale);
      cancelAnimation(translateX);
      cancelAnimation(translateY);

      isPinching.value = true;
      isPinchTransformFrozen.value = false;
      singleFingerGesturesLocked.value = true;
      if (onPinchStart) {
        runOnJS(onPinchStart)();
      }
      const currentTransform = getConstrainedTransform(
        scale.value,
        translateX.value,
        translateY.value,
      );
      scale.value = currentTransform.scale;
      translateX.value = currentTransform.translateX;
      translateY.value = currentTransform.translateY;
      pinchStartScale.value = currentTransform.scale;
      pinchStartTranslateX.value = currentTransform.translateX;
      pinchStartTranslateY.value = currentTransform.translateY;
      pinchStartFocalX.value = event.focalX - containerSize.width / 2;
      pinchStartFocalY.value = event.focalY - containerSize.height / 2;
      didZoomDuringPinch.value = isZoomed(currentTransform.scale);
    })
    .onTouchesUp((event) => {
      if (event.numberOfTouches < 2) {
        freezePinchTransform();
        restoreFrozenPinchTransform();
      }
    })
    .onTouchesCancelled((event) => {
      if (event.numberOfTouches < 2) {
        freezePinchTransform();
        restoreFrozenPinchTransform();
      }
    })
    .onUpdate((event) => {
      const shouldApplyUpdate = shouldApplyPinchUpdate(
        event.numberOfPointers,
        activeTouchCount.value,
      );
      if (!shouldApplyUpdate) {
        freezePinchTransform();
        restoreFrozenPinchTransform();
        return;
      }

      const newScale = clamp(
        pinchStartScale.value * event.scale,
        minScale,
        maxScale,
      );
      const focalX = event.focalX - containerSize.width / 2;
      const focalY = event.focalY - containerSize.height / 2;
      const focalDeltaX = focalX - pinchStartFocalX.value;
      const focalDeltaY = focalY - pinchStartFocalY.value;
      const scaleRatio = newScale / pinchStartScale.value;

      if (isZoomed(newScale)) {
        didZoomDuringPinch.value = true;
      }

      const newTranslateX =
        pinchStartTranslateX.value +
        focalDeltaX +
        (pinchStartFocalX.value - pinchStartTranslateX.value) *
          (1 - scaleRatio);
      const newTranslateY =
        pinchStartTranslateY.value +
        focalDeltaY +
        (pinchStartFocalY.value - pinchStartTranslateY.value) *
          (1 - scaleRatio);

      scale.value = newScale;

      const maxTranslate = getMaxTranslate(newScale);
      translateX.value = clamp(newTranslateX, -maxTranslate.x, maxTranslate.x);
      translateY.value = clamp(newTranslateY, -maxTranslate.y, maxTranslate.y);
    })
    .onEnd((event) => {
      const shouldSettle = shouldSettlePinch(event.numberOfPointers);
      if (!shouldSettle) {
        freezePinchTransform();
        restoreFrozenPinchTransform();
      }
      if (shouldSettle) {
        settlePinch();
      }
    })
    .onFinalize((event) => {
      const shouldSettle = shouldSettlePinch(event.numberOfPointers);
      if (!shouldSettle) {
        freezePinchTransform();
        restoreFrozenPinchTransform();
      }
      if (shouldSettle) {
        settlePinch();
      }
    });

  const panGesture = Gesture.Pan()
    .maxPointers(1)
    .onTouchesDown(rejectSingleFingerGesture)
    .onTouchesMove(rejectSingleFingerGesture)
    .onTouchesUp(rejectSingleFingerGesture)
    .minDistance(5)
    .activeOffsetX([-3, 3])
    .activeOffsetY([-3, 3])
    .onStart(() => {
      panStartTime.value = 0;
      if (isPinching.value || singleFingerGesturesLocked.value) {
        return;
      }

      panStartTime.value = Date.now();
      singleTapCancelledByPan.value = true;

      cancelAnimation(scale);
      cancelAnimation(translateX);
      cancelAnimation(translateY);
      const currentTransform = getConstrainedTransform(
        scale.value,
        translateX.value,
        translateY.value,
      );
      scale.value = currentTransform.scale;
      translateX.value = currentTransform.translateX;
      translateY.value = currentTransform.translateY;
      panStartTranslateX.value = currentTransform.translateX;
      panStartTranslateY.value = currentTransform.translateY;
      if (onPanStart) {
        runOnJS(onPanStart)();
      }
    })
    .onUpdate((event) => {
      const isPanBlocked = isPinching.value || singleFingerGesturesLocked.value;
      if (isPanBlocked) {
        return;
      }

      if (isZoomed(scale.value)) {
        const maxTranslate = getMaxTranslate(scale.value);

        translateX.value = clamp(
          panStartTranslateX.value + event.translationX,
          -maxTranslate.x,
          maxTranslate.x,
        );
        translateY.value = clamp(
          panStartTranslateY.value + event.translationY,
          -maxTranslate.y,
          maxTranslate.y,
        );
      }
    })
    .onEnd((event) => {
      const isPanBlocked = isPinching.value || singleFingerGesturesLocked.value;
      if (isPanBlocked) {
        return;
      }

      const panDurationMs =
        panStartTime.value > 0 ? Date.now() - panStartTime.value : Infinity;
      if (isZoomed(scale.value) && shouldApplyPanMomentum(panDurationMs)) {
        const maxTranslate = getMaxTranslate(scale.value);

        translateX.value = withDecay({
          velocity: event.velocityX,
          clamp: [-maxTranslate.x, maxTranslate.x],
        });
        translateY.value = withDecay({
          velocity: event.velocityY,
          clamp: [-maxTranslate.y, maxTranslate.y],
        });
      }
    });

  const doubleTapGesture = Gesture.Tap()
    .enabled(!singleClickZoom)
    .numberOfTaps(2)
    .maxDuration(DOUBLE_TAP_MAX_DURATION_MS)
    .maxDelay(DOUBLE_TAP_MAX_DELAY_MS)
    .maxDistance(20)
    .onTouchesDown(rejectSingleFingerGesture)
    .onTouchesMove(rejectSingleFingerGesture)
    .onTouchesUp(rejectSingleFingerGesture)
    .onEnd((event) => {
      if (
        !shouldAcceptDoubleTap(
          singleFingerGesturesLocked.value,
          singleTapCancelledByPan.value,
        )
      ) {
        return;
      }

      if (onDoubleTap) {
        runOnJS(onDoubleTap)();
      }

      toggleZoomAtPoint(event.x, event.y);
    });

  const singleTapGesture = Gesture.Tap()
    .numberOfTaps(1)
    .maxDuration(DOUBLE_TAP_MAX_DURATION_MS)
    .maxDistance(20)
    .onTouchesDown(rejectSingleFingerGesture)
    .onTouchesMove(rejectSingleFingerGesture)
    .onTouchesUp(rejectSingleFingerGesture)
    .onEnd((event, success) => {
      if (
        !success ||
        !shouldAcceptSingleTap(
          singleFingerGesturesLocked.value,
          singleTapCancelledByPan.value,
        )
      ) {
        return;
      }

      if (singleClickZoom) {
        toggleZoomAtPoint(event.x, event.y);
        return;
      }

      if (onTap) {
        runOnJS(onTap)();
      }

      if (scale.value <= minScale + 0.01 && onSingleTap) {
        runOnJS(onSingleTap)();
      }
    });

  const tapGestures = Gesture.Simultaneous(doubleTapGesture, singleTapGesture);

  const composedGesture = Gesture.Simultaneous(
    touchTrackerGesture,
    panGesture,
    pinchGesture,
    tapGestures,
  );

  const animatedStyle = useAnimatedStyle(() => {
    const constrainedTransform = getConstrainedTransform(
      scale.value,
      translateX.value,
      translateY.value,
    );

    return {
      transform: [
        { translateX: constrainedTransform.translateX },
        { translateY: constrainedTransform.translateY },
        { scale: constrainedTransform.scale },
      ],
    };
  });

  const handleLayout = (event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;
    if (width <= 0 || height <= 0) {
      return;
    }

    setContainerSize((currentSize) =>
      currentSize.width === width && currentSize.height === height
        ? currentSize
        : { width, height },
    );
  };

  return (
    <ZoomableImageCursor enabled={singleClickZoom} isZoomedIn={isZoomedIn}>
      <GestureDetector gesture={composedGesture}>
        <Animated.View
          onLayout={handleLayout}
          style={[
            {
              flex: 1,
              height: '100%',
              width: '100%',
            },
            animatedStyle,
          ]}
          pointerEvents="auto"
        >
          {children}
        </Animated.View>
      </GestureDetector>
    </ZoomableImageCursor>
  );
}
